import React, { useState, useEffect } from 'react';
import {
  CLINICAL_SEVERITY_GUIDE,
  NON_CLINICAL_SEVERITY_GUIDE,
  RCA_RULES_SUMMARY
} from '../../lib/severityGuideData';

/**
 * SeverityGuideModal
 * Popup แสดงคำอธิบายเกณฑ์ระดับความรุนแรง (Severity Level Guide)
 * มาตรฐาน 2P Safety DSG 2024 ทันตแพทยสภา & สรพ. (HA Thailand)
 * ระบุระยะเวลารักษา/แก้ไข, การรายงานผู้บังคับบัญชา, และเกณฑ์การทำ RCA (Root Cause Analysis)
 */
export const SeverityGuideModal = ({
  isOpen = false,
  onClose = () => {},
  initialTab = 'clinical', // 'clinical' | 'non_clinical' | 'matrix'
  selectedLevel = null,
  onSelectLevel = null
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState('');

  // Sync tab with initialTab when opening
  useEffect(() => {
    if (isOpen) {
      if (initialTab) setActiveTab(initialTab);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, initialTab]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Filter items based on search term
  const filterList = (list) => {
    if (!searchTerm.trim()) return list;
    const term = searchTerm.toLowerCase().trim();
    return list.filter((item) => {
      return (
        item.code.toLowerCase().includes(term) ||
        item.name.toLowerCase().includes(term) ||
        (item.englishName && item.englishName.toLowerCase().includes(term)) ||
        item.group.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term) ||
        (item.examples && item.examples.some((ex) => ex.toLowerCase().includes(term))) ||
        item.rca_type.toLowerCase().includes(term)
      );
    });
  };

  const filteredClinical = filterList(CLINICAL_SEVERITY_GUIDE);
  const filteredNonClinical = filterList(NON_CLINICAL_SEVERITY_GUIDE);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="severity-guide-title"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          width: '100%',
          maxWidth: '920px',
          maxHeight: '90vh',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'fadeInScale 0.2s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ==================== MODAL HEADER ==================== */}
        <div
          style={{
            padding: '18px 24px',
            borderBottom: '1.5px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                width: '42px',
                height: '42px',
                borderRadius: '12px',
                backgroundColor: '#eff6ff',
                color: '#2563eb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '22px',
                boxShadow: '0 2px 6px rgba(37, 99, 235, 0.15)'
              }}
            >
              📋
            </span>
            <div>
              <h2
                id="severity-guide-title"
                style={{
                  margin: 0,
                  fontSize: '1.2em',
                  fontWeight: '800',
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                เกณฑ์ระดับความรุนแรงและการตอบสนองต่ออุบัติการณ์
              </h2>
              <p style={{ margin: '3px 0 0 0', fontSize: '0.82em', color: '#64748b' }}>
                {RCA_RULES_SUMMARY.standardReference}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: '#f1f5f9',
              border: 'none',
              color: '#64748b',
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e2e8f0';
              e.currentTarget.style.color = '#0f172a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            ✕
          </button>
        </div>

        {/* ==================== TOP HIGHLIGHT RCA SUMMARY BANNER ==================== */}
        <div
          style={{
            padding: '14px 24px',
            backgroundColor: '#fffbeb',
            borderBottom: '1.5px solid #fef3c7',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px' }}>⚡</span>
              <strong style={{ fontSize: '0.92em', color: '#92400e' }}>
                สรุปเกณฑ์ที่ต้องทำ RCA (Root Cause Analysis):
              </strong>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <span
                style={{
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  border: '1px solid #fca5a5',
                  padding: '3px 10px',
                  borderRadius: '9999px',
                  fontSize: '0.78em',
                  fontWeight: '700'
                }}
              >
                🩺 คลินิก: ระดับ E ขึ้นไป (E, F, G, H, I)
              </span>
              <span
                style={{
                  backgroundColor: '#ffedd5',
                  color: '#9a3412',
                  border: '1px solid #fdba74',
                  padding: '3px 10px',
                  borderRadius: '9999px',
                  fontSize: '0.78em',
                  fontWeight: '700'
                }}
              >
                🏢 ทั่วไป: ระดับ 3 ขึ้นไป (3, 4, 5)
              </span>
            </div>
          </div>
          <div style={{ fontSize: '0.82em', color: '#78350f', lineHeight: '1.45', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span>
              ⏱️ <strong>รายงานด่วน:</strong> ระดับวิกฤต (G, H, I / 4, 5) รายงานผู้บริหารทันที &lt; 1-2 ชม. | ระดับอันตราย (E, F / 3) รายงานใน 2-4 ชม.
            </span>
            <span>
              📋 <strong>กรอบเวลาทำ RCA:</strong> Mini-RCA (E, 3) ภายใน 7-14 วัน | Full RCA (F, 4) ภายใน 7 วัน | Sentinel Event ภายใน 24-48 ชม.
            </span>
          </div>
        </div>

        {/* ==================== TABS & SEARCH BAR ==================== */}
        <div
          style={{
            padding: '12px 24px',
            borderBottom: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          {/* Navigation Tabs */}
          <div style={{ display: 'flex', gap: '6px', backgroundColor: '#f1f5f9', padding: '4px', borderRadius: '12px' }}>
            <button
              type="button"
              onClick={() => setActiveTab('clinical')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: activeTab === 'clinical' ? '700' : '500',
                fontSize: '0.85em',
                backgroundColor: activeTab === 'clinical' ? '#ffffff' : 'transparent',
                color: activeTab === 'clinical' ? '#2563eb' : '#64748b',
                boxShadow: activeTab === 'clinical' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              🩺 คลินิก (Level A - I)
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('non_clinical')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: activeTab === 'non_clinical' ? '700' : '500',
                fontSize: '0.85em',
                backgroundColor: activeTab === 'non_clinical' ? '#ffffff' : 'transparent',
                color: activeTab === 'non_clinical' ? '#ea580c' : '#64748b',
                boxShadow: activeTab === 'non_clinical' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              🏢 ทั่วไป/สิ่งแวดล้อม (1 - 5)
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('matrix')}
              style={{
                padding: '6px 14px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: activeTab === 'matrix' ? '700' : '500',
                fontSize: '0.85em',
                backgroundColor: activeTab === 'matrix' ? '#ffffff' : 'transparent',
                color: activeTab === 'matrix' ? '#0f172a' : '#64748b',
                boxShadow: activeTab === 'matrix' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
            >
              📊 ตารางสรุปเปรียบเทียบ
            </button>
          </div>

          {/* Search Box */}
          <div style={{ position: 'relative', minWidth: '220px' }}>
            <span
              style={{
                position: 'absolute',
                left: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#94a3b8',
                fontSize: '14px'
              }}
            >
              🔍
            </span>
            <input
              type="text"
              placeholder="ค้นหา เช่น broken file, เข็มตำ, RCA..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '7px 10px 7px 32px',
                borderRadius: '10px',
                border: '1.5px solid #cbd5e1',
                fontSize: '0.82em',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* ==================== MODAL BODY (SCROLLABLE) ==================== */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 24px',
            backgroundColor: '#f8fafc',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          {/* TAB 1: CLINICAL GUIDE */}
          {activeTab === 'clinical' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {filteredClinical.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                  ไม่พบข้อมูลระดับความรุนแรงที่ตรงกับ &ldquo;{searchTerm}&rdquo;
                </div>
              ) : (
                filteredClinical.map((item) => (
                  <SeverityLevelCard
                    key={item.code}
                    item={item}
                    isTarget={selectedLevel === item.code}
                    onSelectLevel={onSelectLevel}
                    onClose={onClose}
                  />
                ))
              )}
            </div>
          )}

          {/* TAB 2: NON-CLINICAL GUIDE */}
          {activeTab === 'non_clinical' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {filteredNonClinical.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                  ไม่พบข้อมูลระดับความรุนแรงที่ตรงกับ &ldquo;{searchTerm}&rdquo;
                </div>
              ) : (
                filteredNonClinical.map((item) => (
                  <SeverityLevelCard
                    key={item.code}
                    item={item}
                    isTarget={selectedLevel === item.code}
                    onSelectLevel={onSelectLevel}
                    onClose={onClose}
                  />
                ))
              )}
            </div>
          )}

          {/* TAB 3: MATRIX SUMMARY TABLE */}
          {activeTab === 'matrix' && (
            <SeverityMatrixView onSelectLevel={onSelectLevel} onClose={onClose} />
          )}
        </div>

        {/* ==================== MODAL FOOTER ==================== */}
        <div
          style={{
            padding: '14px 24px',
            borderTop: '1.5px solid #e2e8f0',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}
        >
          <div style={{ fontSize: '0.78em', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🛡️ ทันตกรรมปลอดภัย 2P Safety: Patient & Personnel Safety</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 20px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#0f172a',
              color: '#ffffff',
              fontSize: '0.88em',
              fontWeight: '700',
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.2)',
              transition: 'background 0.15s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#1e293b')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#0f172a')}
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Level Detail Card Sub-component
 */
const SeverityLevelCard = ({ item, isTarget, onSelectLevel, onClose }) => {
  return (
    <div
      id={`severity-card-${item.code}`}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: isTarget
          ? `2.5px solid ${item.color}`
          : `1.5px solid ${item.badgeBorder || '#e2e8f0'}`,
        boxShadow: isTarget
          ? `0 6px 20px ${item.color}35`
          : '0 2px 6px rgba(0, 0, 0, 0.03)',
        padding: '16px 18px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        transition: 'all 0.15s ease',
        position: 'relative'
      }}
    >
      {/* Header Row: Badge, Name, Group */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '9999px',
              backgroundColor: item.badgeBg || '#f1f5f9',
              border: `1.5px solid ${item.badgeBorder || item.color}`,
              color: item.textColor || item.color,
              fontWeight: '800',
              fontSize: '0.92em',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: item.color
              }}
            />
            ระดับ {item.code}
          </span>

          <span
            style={{
              fontSize: '0.76em',
              padding: '2px 8px',
              borderRadius: '6px',
              backgroundColor: '#f1f5f9',
              color: '#475569',
              fontWeight: '600'
            }}
          >
            {item.group}
          </span>

          <h3
            style={{
              margin: 0,
              fontSize: '1em',
              fontWeight: '700',
              color: '#0f172a'
            }}
          >
            {item.name}
          </h3>
        </div>

        {/* RCA Requirement Pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {item.rca_required ? (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                backgroundColor: '#fef2f2',
                border: '1.5px solid #f87171',
                color: '#b91c1c',
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '0.78em',
                fontWeight: '800'
              }}
            >
              <span>⚠️</span>
              <span>ต้องทำ RCA ({item.rca_timeline})</span>
            </span>
          ) : (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                color: '#15803d',
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '0.76em',
                fontWeight: '600'
              }}
            >
              <span>🛡️</span>
              <span>ไม่ต้องทำ RCA</span>
            </span>
          )}

          {onSelectLevel && (
            <button
              type="button"
              onClick={() => {
                onSelectLevel(item.code);
                onClose();
              }}
              style={{
                padding: '4px 10px',
                borderRadius: '8px',
                backgroundColor: item.color,
                color: '#ffffff',
                border: 'none',
                fontSize: '0.76em',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              เลือกระดับนี้ ↵
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      <div style={{ fontSize: '0.86em', color: '#334155', lineHeight: '1.5' }}>
        {item.description}
        {item.englishName && (
          <span style={{ display: 'block', fontSize: '0.84em', color: '#64748b', fontStyle: 'italic', marginTop: '2px' }}>
            ({item.englishName})
          </span>
        )}
      </div>

      {/* 3 Metric Action Columns */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '10px',
          backgroundColor: '#f8fafc',
          padding: '10px 14px',
          borderRadius: '10px',
          border: '1px solid #e2e8f0'
        }}
      >
        {/* Action Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ fontSize: '0.74em', fontWeight: '700', color: '#0369a1', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>⏱️ การรักษา / แก้ไขเฉพาะหน้า</span>
          </div>
          <div style={{ fontSize: '0.84em', fontWeight: '700', color: '#0f172a' }}>
            {item.action_timeline}
          </div>
        </div>

        {/* Reporting Timeline */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ fontSize: '0.74em', fontWeight: '700', color: '#b45309', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>📢 การรายงานผู้บังคับบัญชา</span>
          </div>
          <div style={{ fontSize: '0.84em', fontWeight: '700', color: '#0f172a' }}>
            {item.reporting_timeline}
          </div>
        </div>

        {/* RCA Action */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ fontSize: '0.74em', fontWeight: '700', color: item.rca_required ? '#b91c1c' : '#15803d', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span>🔍 การทำ Root Cause Analysis (RCA)</span>
          </div>
          <div style={{ fontSize: '0.84em', fontWeight: '700', color: item.rca_required ? '#991b1b' : '#334155' }}>
            {item.rca_type}
            {item.rca_required && (
              <span style={{ fontWeight: '500', color: '#b91c1c', marginLeft: '4px' }}>
                ({item.rca_timeline})
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Examples Section */}
      {item.examples && item.examples.length > 0 && (
        <div style={{ fontSize: '0.8em', color: '#475569' }}>
          <strong style={{ color: '#1e293b' }}>💡 ตัวอย่างเหตุการณ์ในงานทันตกรรม:</strong>
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', lineHeight: '1.45' }}>
            {item.examples.map((example, idx) => (
              <li key={idx} style={{ marginBottom: '2px' }}>
                {example}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Matrix Comparison View (Tab 3)
 */
const SeverityMatrixView = ({ onSelectLevel, onClose }) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Clinical Section */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1.5px solid #e2e8f0', padding: '16px', overflowX: 'auto' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1.05em', color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🩺 ตารางเปรียบเทียบความเสี่ยงทางคลินิก (Level A - I)</span>
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em', textAlign: 'left' }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={{ padding: '8px 10px', width: '70px' }}>ระดับ</th>
              <th style={{ padding: '8px 10px', width: '140px' }}>กลุ่มอันตราย</th>
              <th style={{ padding: '8px 10px', minWidth: '180px' }}>การรักษา/แก้ไขเฉพาะหน้า</th>
              <th style={{ padding: '8px 10px', minWidth: '180px' }}>การรายงานผู้บังคับบัญชา</th>
              <th style={{ padding: '8px 10px', minWidth: '170px' }}>เกณฑ์ทำ RCA</th>
              {onSelectLevel && <th style={{ padding: '8px 10px', width: '70px' }}>เลือก</th>}
            </tr>
          </thead>
          <tbody>
            {CLINICAL_SEVERITY_GUIDE.map((row) => (
              <tr
                key={row.code}
                style={{
                  borderBottom: '1px solid #f1f5f9',
                  backgroundColor: row.rca_required ? '#fffbfb' : '#ffffff'
                }}
              >
                <td style={{ padding: '8px 10px', fontWeight: '800' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      backgroundColor: row.badgeBg,
                      color: row.textColor || row.color,
                      border: `1px solid ${row.badgeBorder}`
                    }}
                  >
                    Level {row.code}
                  </span>
                </td>
                <td style={{ padding: '8px 10px', color: '#475569', fontWeight: '600' }}>
                  {row.group.split(' ')[0]}
                </td>
                <td style={{ padding: '8px 10px', color: '#0369a1', fontWeight: '600' }}>
                  {row.action_timeline}
                </td>
                <td style={{ padding: '8px 10px', color: '#b45309', fontWeight: '600' }}>
                  {row.reporting_timeline}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {row.rca_required ? (
                    <span style={{ color: '#b91c1c', fontWeight: '700' }}>
                      ⚠️ {row.rca_type} ({row.rca_timeline})
                    </span>
                  ) : (
                    <span style={{ color: '#15803d' }}>
                      🛡️ {row.rca_type}
                    </span>
                  )}
                </td>
                {onSelectLevel && (
                  <td style={{ padding: '8px 10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectLevel(row.code);
                        onClose();
                      }}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        border: `1px solid ${row.color}`,
                        backgroundColor: '#ffffff',
                        color: row.color,
                        fontSize: '0.76em',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      เลือก
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Non-Clinical Section */}
      <div style={{ backgroundColor: '#ffffff', borderRadius: '14px', border: '1.5px solid #e2e8f0', padding: '16px', overflowX: 'auto' }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '1.05em', color: '#c2410c', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>🏢 ตารางเปรียบเทียบความเสี่ยงทั่วไป/สิ่งแวดล้อม (Level 1 - 5)</span>
        </h4>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82em', textAlign: 'left' }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={{ padding: '8px 10px', width: '70px' }}>ระดับ</th>
              <th style={{ padding: '8px 10px', width: '140px' }}>กลุ่มความรุนแรง</th>
              <th style={{ padding: '8px 10px', minWidth: '180px' }}>การแก้ไขเฉพาะหน้า</th>
              <th style={{ padding: '8px 10px', minWidth: '180px' }}>การรายงาน</th>
              <th style={{ padding: '8px 10px', minWidth: '170px' }}>เกณฑ์ทำ RCA</th>
              {onSelectLevel && <th style={{ padding: '8px 10px', width: '70px' }}>เลือก</th>}
            </tr>
          </thead>
          <tbody>
            {NON_CLINICAL_SEVERITY_GUIDE.map((row) => (
              <tr
                key={row.code}
                style={{
                  borderBottom: '1px solid #f1f5f9',
                  backgroundColor: row.rca_required ? '#fffbfb' : '#ffffff'
                }}
              >
                <td style={{ padding: '8px 10px', fontWeight: '800' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      backgroundColor: row.badgeBg,
                      color: row.textColor || row.color,
                      border: `1px solid ${row.badgeBorder}`
                    }}
                  >
                    Level {row.code}
                  </span>
                </td>
                <td style={{ padding: '8px 10px', color: '#475569', fontWeight: '600' }}>
                  {row.name}
                </td>
                <td style={{ padding: '8px 10px', color: '#0369a1', fontWeight: '600' }}>
                  {row.action_timeline}
                </td>
                <td style={{ padding: '8px 10px', color: '#b45309', fontWeight: '600' }}>
                  {row.reporting_timeline}
                </td>
                <td style={{ padding: '8px 10px' }}>
                  {row.rca_required ? (
                    <span style={{ color: '#b91c1c', fontWeight: '700' }}>
                      ⚠️ {row.rca_type} ({row.rca_timeline})
                    </span>
                  ) : (
                    <span style={{ color: '#15803d' }}>
                      🛡️ {row.rca_type}
                    </span>
                  )}
                </td>
                {onSelectLevel && (
                  <td style={{ padding: '8px 10px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        onSelectLevel(row.code);
                        onClose();
                      }}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '6px',
                        border: `1px solid ${row.color}`,
                        backgroundColor: '#ffffff',
                        color: row.color,
                        fontSize: '0.76em',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      เลือก
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SeverityGuideModal;
